import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise, MapPin, X, Crosshair } from "@phosphor-icons/react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const statusColor = {
  new: "#f97316",           // fresh — orange
  pool: "#a855f7",          // needs assignment — purple
  assigned: "#3b82f6",      // blue
  scheduled: "#0ea5e9",     // sky
  dispatched: "#8b5cf6",    // violet
  in_progress: "#00C4CC",   // brand teal
  on_site: "#14b8a6",       // teal-dark
  completed: "#22c55e",     // green
  follow_up: "#eab308",     // yellow
  needs_info: "#f59e0b",    // amber
  canceled: "#94a3b8",      // slate
  cancelled: "#94a3b8",     // (both spellings)
  landing_pad: "#e11d48",   // ClosureRadar — rose
};

// Color-coded map pin (SVG teardrop). Replaces the old logo-image marker so
// the pin color IS the status at a glance — no more mystery photo icons.
const makeIcon = (color) => L.divIcon({
  className: "field-radar-pin",
  html: `
    <div style="width:32px;height:44px;position:relative;filter:drop-shadow(0 3px 4px rgba(0,0,0,0.45));">
      <svg viewBox="0 0 32 44" width="32" height="44" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1 C7.716 1 1 7.716 1 16 c0 10.5 15 27 15 27 s15 -16.5 15 -27 C31 7.716 24.284 1 16 1 z"
              fill="${color}" stroke="#0f172a" stroke-width="2"/>
        <circle cx="16" cy="16" r="5" fill="#ffffff"/>
      </svg>
    </div>`,
  iconSize: [32, 44],
  iconAnchor: [16, 42],
  popupAnchor: [0, -40],
});

// Handles map clicks when in "drop pin" mode
function ClickCatcher({ enabled, onPick }) {
  useMapEvents({
    click: (e) => { if (enabled) onPick(e.latlng); },
  });
  return null;
}

export default function MapView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [pinJobId, setPinJobId] = useState("");     // job currently being pin-dropped
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api.get("/jobs").then(r => setJobs(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Tenant metro center for detecting off-metro pins on the client.
  // Defaults to Philadelphia to match backend default_location.
  const TENANT_CENTER = { lat: 39.9526, lng: -75.1652 };
  const OFF_METRO_MI = 100;
  const _dist = (a, b) => {
    if (!a || !b || a.lat == null || a.lng == null) return 0;
    const R = 3958.8;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const p1 = a.lat * Math.PI / 180;
    const p2 = b.lat * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };

  const offMetro = jobs.filter(j => {
    const c = j.geocoded;
    return c && c.lat != null && c.lng != null && _dist(TENANT_CENTER, c) > OFF_METRO_MI;
  });

  const mapped = jobs.map(j => {
    const c = j.geocoded || j.clock_in_location;
    if (!c || !c.lat || !c.lng) return null;
    // Suppress off-metro pins from the visible map — they're almost certainly bad
    // geocodes and should be fixed via "Locate Addresses" before showing.
    if (_dist(TENANT_CENTER, c) > OFF_METRO_MI) return null;
    return { ...j, _coord: c };
  }).filter(Boolean);

  const unmapped = jobs.filter(j => {
    const c = j.geocoded || j.clock_in_location;
    if (!c || !c.lat || !c.lng) return true;
    return _dist(TENANT_CENTER, c) > OFF_METRO_MI;
  });

  const missing = unmapped.length;
  const strayCount = offMetro.length;

  const backfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    toast.info("Geocoding missing addresses… this may take up to a minute.");
    try {
      const { data } = await api.post("/jobs/backfill-geocode");
      // Second pass: sweep any stray off-metro pins (San Diego, Kansas, etc.)
      let extra = 0;
      if (strayCount > 0) {
        const r2 = await api.post("/jobs/backfill-geocode?off_metro=true");
        extra = r2.data?.updated || 0;
      }
      const totalFixed = data.updated + extra;
      toast.success(`Fixed ${totalFixed} pin${totalFixed === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} could not be located` : ""}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBackfilling(false); }
  };

  const startPinDrop = (jobId) => {
    setPinJobId(jobId);
    setPickerOpen(false);
    toast.info("Click anywhere on the map to drop the pin for this job.", { duration: 4000 });
  };

  const cancelPinDrop = () => setPinJobId("");

  const onPickLocation = async ({ lat, lng }) => {
    if (!pinJobId) return;
    const job = jobs.find(j => j.id === pinJobId);
    try {
      await api.post(`/jobs/${pinJobId}/set-location?lat=${lat}&lng=${lng}`);
      toast.success(`Pin dropped for "${job?.title || "job"}".`);
      setPinJobId("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save location.");
    }
  };

  const center = mapped[0]?._coord || { lat: 39.8283, lng: -98.5795 };
  const zoom = mapped.length > 0 ? 10 : 4;
  const pinningJob = pinJobId ? jobs.find(j => j.id === pinJobId) : null;

  return (
    <AdminShell>
      <div className="p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight">Dispatch Map</h1>
            <p className="mt-1 text-slate-500 font-medium">
              {loading ? "Loading..." : `${mapped.length} of ${jobs.length} jobs on the map`}
              {!loading && missing > 0 && (
                <span className="ml-2 text-xs text-orange-600 font-bold">
                  · {missing} needing correction
                  {strayCount > 0 && ` (${strayCount} stray off-metro)`}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {missing > 0 && (
              <>
                <button onClick={backfill} disabled={backfilling} data-testid="backfill-map-btn"
                  className="tactile-btn h-11 px-4 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                  <ArrowsClockwise weight="bold" size={16} className={backfilling ? "animate-spin" : ""}/>
                  {backfilling ? "Locating..." : `Locate ${missing} Address${missing === 1 ? "" : "es"}`}
                </button>
                <button onClick={() => setPickerOpen(true)} data-testid="open-manual-pin-btn"
                  className="tactile-btn h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 bg-white">
                  <MapPin weight="bold" size={16}/> Drop Pin Manually
                </button>
              </>
            )}
            <div className="flex items-center gap-3 text-xs font-black uppercase tracking-wider">
              {Object.entries(statusColor).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: v }}/>
                  {k.replace("_", " ")}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Active pin-drop banner */}
        {pinningJob && (
          <div className="mt-4 rugged-card p-4 flex items-center justify-between bg-yellow-50 border-yellow-400" data-testid="pin-drop-banner">
            <div className="flex items-center gap-3">
              <Crosshair weight="bold" size={22} className="text-yellow-600"/>
              <div>
                <div className="font-display font-black text-sm">
                  Placing pin for: <span className="text-[#00C4CC]">{pinningJob.title}</span>
                </div>
                <div className="text-xs text-slate-600 font-medium">
                  {pinningJob.property_address || "No address"} — click anywhere on the map to drop.
                </div>
              </div>
            </div>
            <button onClick={cancelPinDrop} data-testid="cancel-pin-drop-btn"
              className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 bg-white hover:bg-[#0f172a] hover:text-white transition-colors">
              <X weight="bold" size={14}/> Cancel
            </button>
          </div>
        )}

        <div
          className={`mt-6 rugged-card overflow-hidden ${pinningJob ? "ring-4 ring-yellow-400" : ""}`}
          style={{ height: "calc(100vh - 240px)", cursor: pinningJob ? "crosshair" : "auto" }}
          data-testid="map-container"
        >
          <MapContainer center={[center.lat, center.lng]} zoom={zoom} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickCatcher enabled={!!pinningJob} onPick={onPickLocation}/>
            {mapped.map(j => (
              <Marker key={j.id} position={[j._coord.lat, j._coord.lng]} icon={makeIcon(statusColor[j.status] || "#64748b")}>
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-display font-black text-base">{j.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{j.property_address}</div>
                    <div className="text-xs mt-1">
                      <span className="font-bold uppercase">{j.status.replace("_", " ")}</span>
                      {" · "}
                      <span>{j.assigned_to_name || "Unassigned"}</span>
                    </div>
                    <Link to={`/admin/jobs/${j.id}`} className="mt-2 inline-block text-xs font-black uppercase text-[#00C4CC] underline">
                      Open job →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Modal: pick which unlocated job to place */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="unmapped-picker-modal">
          <div className="bg-white w-full max-w-lg rugged-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Which job needs a pin?</h3>
              <button onClick={() => setPickerOpen(false)} className="text-slate-500 hover:text-[#0f172a]" data-testid="close-unmapped-picker">
                <X weight="bold" size={22}/>
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-600 font-medium">
              Pick a job whose address geocoding couldn't resolve. You'll then click the map to save its location.
            </p>
            <div className="mt-4 max-h-[420px] overflow-y-auto space-y-2 -mx-1 px-1" data-testid="unmapped-jobs-list">
              {unmapped.length === 0 ? (
                <div className="text-sm text-slate-500 font-medium py-4 text-center">All jobs already have coordinates.</div>
              ) : unmapped.map(j => (
                <button key={j.id}
                  onClick={() => startPinDrop(j.id)}
                  data-testid={`pick-unmapped-${j.id}`}
                  className="w-full text-left border-2 border-[#0f172a] bg-white p-3 hover:bg-slate-50 transition-colors">
                  <div className="font-display font-black text-sm">{j.title}</div>
                  <div className="text-xs text-slate-600 font-medium mt-0.5">{j.property_address || "No address on file"}</div>
                  <div className="text-[11px] font-black uppercase tracking-wider mt-1" style={{ color: statusColor[j.status] || "#64748b" }}>
                    {j.status.replace("_", " ")} · {j.assigned_to_name || "Unassigned"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
